import { getUrlQueryParam } from "@transact-open-ux/core";
import {
  Formik,
  FormikConfig,
  FormikProps,
  getIn,
  yupToFormErrors
} from "formik";
import React, { FunctionComponent, useEffect, useRef } from "react";
import { useTransactContext } from "../providers/TransactProvider";

export type Schema = any;

export interface FormProps extends Partial<FormikConfig<any>> {
  /**
   * Specify whether to send a "Form Receipt" email message to the specified user submissionEmailAddress property
   */
  sendEmailFormReceipt?: boolean;
  /**
   * The email address of the user in which the "Form Receipt" will be sent
   */
  submissionEmailAddress?: string;
  /**
   * A transaction milestone event to record with the transaction, maximum length should be 100 characters
   */
  submissionMilestone?: string;
  /**
   * The render prop can be used the render the form, you can access all the render props from the Formik component
   * here.
   * @param props
   */
  render?: (props: FormikProps<any>) => React.ReactNode;
  /**
   * The children prop can be used the render the form, you can access all the render props from the Formik component
   * here.
   * @param props
   */
  children?: (props: object) => React.ReactNode;
  /**
   * [A Yup schema](https://github.com/jquense/yup) or a function that returns a Yup schema.
   * This is used for validation. Errors are mapped by key to the inner component's `errors`.
   * Its keys should match those of `values`
   */
  validationSchema?: Schema | (() => Schema);
  /**
   * A component that will render when the form is loading the initial data
   */
  loadingComponent?: string | React.ComponentType<any>;
}

/**
 * @private
 * @param value
 */
const hasValue = (value): boolean => {
  return typeof value === "number" || typeof value === "boolean" || !!value;
};

// Used by component to determine whether a field has been validated at least once
let validatedOnce = {};

/**
 * The `TransactForm` Component is a light wrapper on top of [Formik's](https://jaredpalmer.com/formik) `Formik` component.
 * <br>
 * It adds page level validation functionality with Yup and transact support to Formik,
 * while maintaining all of Formik's flexibility and functionality.
 * <br>
 * You can read more about the properties inherited from the `Formik` component [here](https://jaredpalmer.com/formik/docs/api/formik).
 */
const TransactForm: FunctionComponent<FormProps> = props => {
  const formikRef: any = useRef({ current: null });
  const {
    render,
    children,
    validate,
    validationSchema,
    loadingComponent: LoadingComponent,
    sendEmailFormReceipt,
    submissionEmailAddress,
    submissionMilestone,
    ...rest
  } = props;
  const {
    formData = {},
    formLoaded,
    formStatus,
    userSubmit,
    setFormApi
  } = useTransactContext();

  const handleSubmit = values => {
    if (formStatus !== "Completed") {
      userSubmit(
        values,
        sendEmailFormReceipt,
        submissionEmailAddress,
        submissionMilestone
      );
    }
  };

  const renderFormComponent = formikProps => {
    if (setFormApi) {
      setFormApi(formikProps);
    }

    return typeof render === "function"
      ? render({
          ...props,
          ...formikProps
        })
      : typeof children === "function"
      ? children({
          ...props,
          ...formikProps
        })
      : null;
  };

  const handleYupErrors = validationError => {
    const { inner } = validationError;
    const { fields: mountedFields = {}, state = {} } = formikRef.current;
    const { touched, isSubmitting } = state;
    const errorMessages = [];

    validationError.inner = inner.filter(innerError => {
      const { path, value, message } = innerError;
      const isTouched = !!getIn(touched, path);
      let isMounted;

      if (Array.isArray(value)) {
        isMounted = mountedFields.hasOwnProperty(`${path}[0]`);
      } else if (value && typeof value === "object") {
        isMounted = Object.keys(value).every(key => {
          return mountedFields.hasOwnProperty(`${path}.${key}`);
        });
      } else {
        isMounted = mountedFields.hasOwnProperty(path);
      }

      // submitting or touched and validated once or has a value
      const keepError =
        isMounted &&
        (isSubmitting ||
          (isTouched && (validatedOnce[path] || hasValue(value))));

      if (keepError) {
        if (!validatedOnce[path]) {
          validatedOnce[path] = true;
        }

        errorMessages.push(message);
      }

      return keepError;
    });

    if (errorMessages.length) {
      validationError.errors = errorMessages;
      throw yupToFormErrors(validationError);
    }
  };

  const handleValidation = values => {
    if (validationSchema) {
      const { fields: mountedFields = {}, state = {} } = formikRef.current;
      const { touched } = state;

      // loop mounted fields to mark which ones will be validated once
      Object.keys(mountedFields).forEach(path => {
        const isTouched = !!getIn(touched, path);

        if (
          isTouched &&
          hasValue(getIn(values, path)) &&
          !validatedOnce[path]
        ) {
          validatedOnce[path] = true;
        }
      });

      return validationSchema
        .validate(values, {
          abortEarly: false
        })
        .then(() => ({}))
        .catch(handleYupErrors);
    }

    return validate(values);
  };

  // ensures if the component is re-mounted the validation will be reset
  useEffect(() => {
    validatedOnce = {};
  }, []);

  return formLoaded ? (
    <Formik
      initialValues={{
        $SaveChallenge: {
          TrackingCode: getUrlQueryParam("trackingCode")
        },
        ...formData
      }}
      {...rest}
      validate={(validationSchema || validate) && handleValidation}
      enableReinitialize
      onSubmit={handleSubmit}
      render={renderFormComponent}
      ref={formikRef}
    />
  ) : LoadingComponent ? (
    <LoadingComponent />
  ) : null;
};

export default TransactForm;
